import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Flag } from "lucide-react";
import { toast } from "sonner";

const REASONS = ["Spam", "Inappropriate or offensive", "Misleading or scam", "Harassment", "Other"];

// Friendly labels — raw target types (e.g. "collective_message") must never
// leak into UI copy. Unmapped types read as plain "content".
const TYPE_LABELS = {
  story: "story",
  content: "post",
  opportunity: "opportunity",
  user: "profile",
  message: "message",
  feedback: "feedback",
  collective: "collective",
  collective_message: "message",
  collective_post: "post",
  group_message: "message",
  gathering: "gathering",
  team: "team",
};

export const ReportButton = ({ targetType, targetId, label = "Report", className = "" }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const typeLabel = TYPE_LABELS[targetType] || "content";

  if (!user) return null;

  const submit = async () => {
    if (!reason) return toast.error("Pick a reason");
    try {
      await api.report(targetType, targetId, reason, note);
      setOpen(false);
      setReason("");
      setNote("");
      toast.success("Reported — our moderators will take a look");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`report-btn-${targetType}-${targetId}`}
          className={`text-xs text-neutral-400 hover:text-red-500 inline-flex items-center gap-1 transition-colors ${className}`}
          aria-label={`Report this ${typeLabel}`}
        >
          <Flag className="w-3.5 h-3.5" /> {label}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Report this {typeLabel}</DialogTitle>
          <DialogDescription>Reports are reviewed by moderators. Misuse of reporting may affect your account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason-select" className="h-11">
              <SelectValue placeholder="Why are you reporting this?" />
            </SelectTrigger>
            <SelectContent>
              {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea
            data-testid="report-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything else we should know? (optional)"
            rows={3}
          />
          <div className="flex justify-end">
            <Button data-testid="report-submit-btn" onClick={submit} className="rounded-full bg-red-600 hover:bg-red-700">
              Submit report
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
